<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledDateInput from "@/components/StyledDateInput.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import useInvalidateQueryData from "@/composables/useInvalidateQueryData";
import { GenderTypes } from "@/enums/genderTypes";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import EditUserLogoVue from "@/modules/users/components/edit/EditUserLogo.vue";
import useUser from "@/modules/users/composables/useUser";
import type { User } from "@/modules/users/interfaces/user";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { reactive, watch } from "vue";
import { useToast } from "vue-toastification";
import useInternalUser from "../../../composables/useInternalUser";

interface Props {
  user: User;
}

const props = defineProps<Props>();
const { invalidateQuery } = useInvalidateQueryData();
const toast = useToast();

const rules = {
  name: {
    required: helpers.withMessage("El nombre es requerido", required),
  },
  lastname: {
    required: helpers.withMessage("El apellido es requerido", required),
  },
  birthDate: {
    required: helpers.withMessage(
      "La fecha de nacimiento es requerida",
      required,
    ),
    requiredIf: helpers.withMessage(
      "El usuario debe ser mayor de edad",
      (value: string) => {
        const birthDate = new Date(value);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const month = today.getMonth() - birthDate.getMonth();

        if (
          month < 0 ||
          (month === 0 && today.getDate() < birthDate.getDate())
        ) {
          return age - 1 >= 18;
        }

        return age >= 18;
      },
    ),
  },
  gender: {
    required: helpers.withMessage("El género es requerido", required),
  },
};

const form = reactive({
  id: props.user.id,
  name: props.user.name,
  secondName: props.user.secondName,
  lastname: props.user.lastname,
  secondLastname: props.user.secondLastname,
  avatar: undefined,
  birthDate: props.user.birthDate,
  gender: props.user.gender,
  companyId: props.user.companyId,
});

const v$ = useVuelidate(rules, form);

const { updateUserAsync, updateUserLoading } = useInternalUser({
  immediate: false,
});

const onSubmit = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  await updateUserAsync(form);
  invalidateQuery(["user-internal", props.user.id]);
};
</script>

<template>
  <RoundedBox class="bg-white">
    <h2 class="text-xl font-medium">Datos Generales</h2>

    <EditUserLogoVue class="pb-10" :user="props.user" />

    <form
      v-if="props.user"
      @submit.prevent="onSubmit"
      class="grid gap-[1.563rem]"
    >
      <div class="grid gap-4">
        <FieldSet>
          <StyledInput is-required label="Nombre" v-model="form.name" />
          <StyledInput label="Segundo nombre" v-model="form.secondName" />
        </FieldSet>

        <FieldSet>
          <StyledInput is-required label="Apellido" v-model="form.lastname" />
          <StyledInput label="Segundo apellido" v-model="form.secondLastname" />
        </FieldSet>

        <StyledDateInput
          label="Fecha de nacimiento"
          v-model="form.birthDate"
          :is-required="true"
        />

        <StyledSelect
          :is-required="true"
          :options="
            Object.values(GenderTypes).map((gender) => ({
              label: gender,
              value: gender,
            }))
          "
          v-model="form.gender"
          label="Género"
        />
      </div>

      <HasPermission
        :name-permission="'update-internal'"
        :entity-type="'App\\Models\\User'"
      >
        <StyledButton
          label="Guardar"
          class="bg-[#253C70] text-white ml-auto"
          type="submit"
          :loading="updateUserLoading"
          :disabled="updateUserLoading"
        />
      </HasPermission>
    </form>
    <!-- <EditUserPhones :user-id="user.id" :phones="user.phones || []" /> -->
  </RoundedBox>
</template>

<script setup lang="ts"></script>

<style scoped></style>
